import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { loginSuccess } from "../../slices/authSlice";
import { Link, useNavigate, useLocation } from "react-router-dom";
import useFormPersistence from "../../hooks/useFormPersistence";
import BackupCodes from "../settings/BackupCodes";
import TwoFactorEnroll from "../settings/TwoFactorEnroll";
import {
  TwoFactorMethod,
  loginEnrollmentApi,
  resendLoginCode,
  verifyLoginCode,
} from "../../services/twoFactorService";
import { API_BASE_URL } from "../../config/apiBaseUrl";

export default function LoginForm() {
  const { values, handleChange, clearForm } = useFormPersistence("loginForm", {
    email: "",
  });

  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [twoFactorToken, setTwoFactorToken] = useState("");
  const [twoFactorMethod, setTwoFactorMethod] = useState<TwoFactorMethod>("app");
  const [code, setCode] = useState("");
  const [info, setInfo] = useState("");
  // Set while the second step still has to be set up before the session starts
  const [setupToken, setSetupToken] = useState("");
  const [appOnly, setAppOnly] = useState(false);
  const [pendingSession, setPendingSession] = useState<any>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const verified = params.get("verified");
    const registered = params.get("registered");

    if (verified === "success") {
      setSuccess("Account successfully verified. You can now log in.");
    } else if (verified === "already") {
      setSuccess("Your account is already verified. Please log in.");
    } else if (verified === "error") {
      setError("Verification failed! Please request a new link.");
    }

    if (registered === "success") {
      setSuccess(
        "Registration successful. Please check your email for confirmation.",
      );
    }
  }, [location.search]);

  const finishLogin = (data: any) => {
    dispatch(
      loginSuccess({
        token: data.accessToken,
        refreshToken: data.refreshToken,
        user: data.user,
      }),
    );

    clearForm();
    setPassword("");
    navigate("/home");
  };

  const backToPassword = () => {
    setTwoFactorToken("");
    setSetupToken("");
    setPendingSession(null);
    setCode("");
    setInfo("");
  };

  const startEnrollment = (data: any) => {
    setTwoFactorToken("");
    setCode("");
    setInfo("");
    setAppOnly(data.requiredMethod === "app");
    setSetupToken(data.twoFactorToken);
  };

  const handleResend = async () => {
    setError("");
    setInfo("");

    try {
      const result = await resendLoginCode(twoFactorToken);
      setInfo(`A new code was sent to ${result.sentTo}.`);
    } catch (err: any) {
      if (err.expired) backToPassword();
      setError(err.message);
    }
  };

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!code.trim()) {
      setError("Please enter your code");
      return;
    }

    try {
      const data = await verifyLoginCode(twoFactorToken, code);

      if (data.twoFactorSetupRequired) {
        startEnrollment(data);
        return;
      }

      finishLogin(data);
    } catch (err: any) {
      if (err.expired) backToPassword();
      setError(err.message || "Login failed");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!values.email || !password) {
      setError("Please enter both email and password");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/users/login`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: values.email, password }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || "Login failed");
        return;
      }

      if (data.twoFactorRequired) {
        setTwoFactorMethod(data.twoFactorMethod === "email" ? "email" : "app");
        setTwoFactorToken(data.twoFactorToken);
        setPassword("");
        return;
      }

      if (data.twoFactorSetupRequired) {
        startEnrollment(data);
        setPassword("");
        return;
      }

      finishLogin(data);
    } catch {
      setError("Network error. Please try again.");
    }
  };

  if (pendingSession) {
    return (
      <div className="mt-6 flex w-full max-w-sm flex-col gap-4 rounded-xl border border-secondary5 bg-white p-8">
        <BackupCodes
          codes={pendingSession.backupCodes}
          doneLabel="Continue"
          onDone={() => finishLogin(pendingSession)}
        />
      </div>
    );
  }

  if (setupToken) {
    return (
      <div className="mt-6 flex w-full max-w-sm flex-col gap-4 rounded-xl border border-secondary5 bg-white p-8">
        <div className="text-center">
          <h1 className="font-heading text-2xl text-dantzBlue">
            Set up two-factor authentication
          </h1>
          <p className="mt-1 text-sm text-secondary4">
            A second step is now required to log in. It takes a minute.
          </p>
        </div>

        <TwoFactorEnroll
          api={loginEnrollmentApi(setupToken)}
          appOnly={appOnly}
          onExpired={backToPassword}
          onDone={(session) => {
            if (session.backupCodes && session.backupCodes.length > 0) {
              setPendingSession(session);
            } else {
              finishLogin(session);
            }
          }}
          onCancel={backToPassword}
        />
      </div>
    );
  }

  if (twoFactorToken) {
    return (
      <form
        onSubmit={handleCodeSubmit}
        className="mt-6 flex w-full max-w-sm flex-col gap-4 rounded-xl border border-secondary5 bg-white p-8"
      >
        <div className="text-center">
          <h1 className="font-heading text-2xl text-dantzBlue">
            Two-factor authentication
          </h1>
          <p className="mt-1 text-sm text-secondary4">
            {twoFactorMethod === "email"
              ? "Enter the 6-digit code we sent to your e-mail address."
              : "Enter the 6-digit code from your authenticator app, or one of your backup codes."}
          </p>
        </div>

        {info && (
          <div className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
            {info}
          </div>
        )}
        {error && (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
            {error}
          </div>
        )}

        <div>
          <label
            htmlFor="login-code"
            className="mb-1 block text-xs font-medium text-gray-600"
          >
            Code
          </label>
          <input
            id="login-code"
            type="text"
            inputMode="text"
            autoComplete="one-time-code"
            autoFocus
            maxLength={12}
            placeholder="123456"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="w-full rounded-lg border border-secondary4 px-4 py-2.5 text-center text-lg tracking-widest focus:border-dantzOrange focus:outline-none focus:ring-2 focus:ring-dantzOrange/20"
          />
        </div>

        <button
          type="submit"
          className="mt-2 w-full rounded-lg bg-dantzOrange py-2.5 font-medium text-white transition hover:brightness-110 active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dantzBlue"
        >
          Verify
        </button>

        {twoFactorMethod === "email" && (
          <button
            type="button"
            onClick={handleResend}
            className="text-center text-sm font-medium text-dantzBlue underline"
          >
            Send a new code
          </button>
        )}

        <button
          type="button"
          onClick={() => {
            backToPassword();
            setError("");
          }}
          className="text-center text-sm font-medium text-dantzBlue underline"
        >
          Back to login
        </button>
      </form>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-6 flex w-full max-w-sm flex-col gap-4 rounded-xl border border-secondary5 bg-white p-8"
    >
      <div className="text-center">
        <h1 className="font-heading text-2xl text-dantzBlue">Welcome back</h1>
        <p className="mt-1 text-sm text-secondary4">
          Log in to your Xpress IP account
        </p>
      </div>

      {success && (
        <div className="rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
          {success}
        </div>
      )}
      {error && (
        <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
          {error}
        </div>
      )}

      <div>
        <label
          htmlFor="login-email"
          className="mb-1 block text-xs font-medium text-gray-600"
        >
          Email
        </label>
        <input
          id="login-email"
          type="email"
          placeholder="Your email"
          value={values.email}
          onChange={(e) => handleChange("email", e.target.value)}
          className="w-full rounded-lg border border-secondary4 px-4 py-2.5 text-sm focus:border-dantzOrange focus:outline-none focus:ring-2 focus:ring-dantzOrange/20"
        />
      </div>

      <div>
        <label
          htmlFor="login-password"
          className="mb-1 block text-xs font-medium text-gray-600"
        >
          Password
        </label>
        <input
          id="login-password"
          type="password"
          placeholder="Your password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full rounded-lg border border-secondary4 px-4 py-2.5 text-sm focus:border-dantzOrange focus:outline-none focus:ring-2 focus:ring-dantzOrange/20"
        />
      </div>

      <button
        type="submit"
        className="mt-2 w-full rounded-lg bg-dantzOrange py-2.5 font-medium text-white transition hover:brightness-110 active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dantzBlue"
      >
        Log in
      </button>

      <p className="text-center text-sm text-secondary4">
        No account yet?{" "}
        <Link to="/register" className="font-medium text-dantzBlue underline">
          Register
        </Link>
      </p>
    </form>
  );
}
